import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Legend, ResponsiveContainer, Sector
} from 'recharts';
import { toast } from 'react-toastify';
import EmployeeProcessService from '../services/EmployeeProcessService';
import { useLoading } from '../Utils/LoadingContext';
import './Home.css';

// ✅ Status mapping with fixed order & colors
const STATUS_MAP = {
  PP: { name: 'Present', color: '#4CAF50', order: 1 },
  AA: { name: 'Absent', color: '#F44336', order: 2 },
  AP: { name: 'First Half', color: '#FF9800', order: 3 },
  PA: { name: 'Second Half', color: '#FFB74D', order: 4 },
  WO: { name: 'Week Off', color: '#9E9E9E', order: 5 },
  HL: { name: 'Holiday', color: '#2196F3', order: 6 }
};

// 🔄 Transform API data into fixed order + labels + percentages
const transformAttendanceData = (data) => {
  const mapped = Object.keys(STATUS_MAP).map((key) => {
    const item = data.find((d) => d.label === key);
    const statusInfo = STATUS_MAP[key];
    return {
      label: key,
      name: statusInfo.name,
      color: statusInfo.color,
      order: statusInfo.order,
      count: item ? item.count : 0,
    };
  });

  // ✅ Only sum required statuses
  const total = mapped.reduce((sum, item) => {
    if (['PP', 'AA', 'AP', 'PA', 'WO', 'HL'].includes(item.label)) {
      return sum + item.count;
    }
    return sum;
  }, 0) || 1;

  return mapped
    .map((item) => ({
      ...item,
      percentage: ((item.count / total) * 100).toFixed(1),
    }))
    .sort((a, b) => a.order - b.order);
};

const renderActiveShape = (props) => {
  const {
    cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload, percent, value,
  } = props;
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;

  return (
    <g>
      <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={fill} fontWeight="bold">
        {payload.name}
      </text>
      <text x={cx} y={cy + 10} dy={8} textAnchor="middle" fill="#333">
        {`${value} (${(percent * 100).toFixed(1)}%)`}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
      />
      <path d={`M${sx},${sy}L${mx},${my}`} stroke={fill} fill="none" />
      <circle cx={mx} cy={my} r={3} fill={fill} stroke="none" />
    </g>
  );
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor="top"
      dominantBaseline="central"
      fontSize={9}   // smaller inside pie
      fontWeight="light"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

export default function Home() {
  const { setLoading } = useLoading();
  const [data, setData] = useState([]);
  const [activeIndexToday, setActiveIndexToday] = useState(0);
  const [activeIndexWeekly, setActiveIndexWeekly] = useState(0);
  const [activeIndexMonthly, setActiveIndexMonthly] = useState(0);
  const [activeIndexGender, setActiveIndexGender] = useState(0);
  const [attendanceCharts, setAttendanceCharts] = useState({ today: [], weekly: [], monthly: [] });
  const [showAttendance, setShowAttendance] = useState(true);
  const [showEmployee, setShowEmployee] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/');
    else {
      handleSearch();
      fetchAttendanceCharts();
    }
  }, []);

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await EmployeeProcessService.getEmployeeChart();
      if (response.data?.result?.length > 0) {
        setData(response.data.result);
      } else alert('Data not found');
    } catch (err) {
      //toast.error('Error fetching employee data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceCharts = async () => {
    try {
      setLoading(true);
      const [today, weekly, monthly] = await Promise.all([
        EmployeeProcessService.getAttendanceChart('1', 'TodayAttendance','1'),
        EmployeeProcessService.getAttendanceChart('1', 'WeeklyAttendance','1'),
        EmployeeProcessService.getAttendanceChart('1', 'LastMonthAttendance','1')
      ]);
      setAttendanceCharts({
        today: transformAttendanceData(today.data.result || []),
        weekly: transformAttendanceData(weekly.data.result || []),
        monthly: transformAttendanceData(monthly.data.result || [])
      });
    } catch {
      //toast.error('Error loading attendance charts');
    } finally {
      setLoading(false);
    }
  };

  const genderData = Object.values(
    data.reduce((acc, item) => {
      const gender = item.gender || 'Unknown';
      acc[gender] = acc[gender] || { name: gender, value: 0 };
      acc[gender].value += 1;
      return acc;
    }, {})
  );

  const ageData = Object.values(
    data.reduce((acc, item) => {
      const age = item.age || 0;
      const range =
        age < 25 ? '<25' :
          age < 35 ? '25-34' :
            age < 50 ? '35-49' : '50+';
      acc[range] = acc[range] || { range, count: 0 };
      acc[range].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => {
    const order = ['<25', '25-34', '35-49', '50+'];
    return order.indexOf(a.range) - order.indexOf(b.range);
  });

  const expData = Object.values(
    data.reduce((acc, item) => {
      const experience = item.experience || 0;
      const range =
        experience < 5 ? '<5' :
          experience < 10 ? '5-9' : '10+';
      acc[range] = acc[range] || { range, count: 0 };
      acc[range].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => {
    const order = ['<5', '5-9', '10+'];
    return order.indexOf(a.range) - order.indexOf(b.range);
  });

  // ✅ Pie rendering with skip 0-values
  const renderAttendancePie = (title, chartData, activeIndex, setActiveIndex) => (
    <div className="card shadow-sm border-0 mb-0">
      <div className="card-body">
        <h5 className="card-title text-center">{title}</h5>
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={chartData.filter((entry) => entry.count > 0)}
                dataKey="count"
                nameKey="label"
                outerRadius={110}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                label={renderCustomizedLabel}
              >
                {chartData.filter((entry) => entry.count > 0).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                payload={chartData
                  .filter((item) => item.count > 0)
                  .map((item) => ({
                    value: `${item.name} (${item.percentage}%)`,
                    type: 'square',
                    color: item.color,
                  }))}
                wrapperStyle={{ fontSize: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container py-1">
      <div
        className="col-12 mb-1 mt-1  border-bottom pb-2 d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setShowAttendance(prev => !prev)}
      >
        <h5 className="text-left text-success">Attendance Charts</h5>
        <span>{showAttendance ? '▼' : '►'}</span>
      </div>

      {showAttendance && (
        <div className="row g-4">
          <div className="col-md-4">
            <div className="card shadow-sm border-0 attendance-card">
              {renderAttendancePie("Today's Punch", attendanceCharts.today, activeIndexToday, setActiveIndexToday)}
            </div>
          </div>
          <div className="col-md-4">
            <div className="card shadow-sm border-0 attendance-card">
              {renderAttendancePie("Weekly Attendance", attendanceCharts.weekly, activeIndexWeekly, setActiveIndexWeekly)}
            </div>
          </div>
          <div className="col-md-4">
            <div className="card shadow-sm border-0 attendance-card">
              {renderAttendancePie("Last Month Attendance", attendanceCharts.monthly, activeIndexMonthly, setActiveIndexMonthly)}
            </div>
          </div>
        </div>
      )}

      <div
        className="col-12 mb-3 mt-3 border-bottom pb-2 d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setShowEmployee(prev => !prev)}
      >
        <h5 className="text-left text-primary">Employee Dashboard</h5>
        <span>{showEmployee ? '▼' : '►'}</span>
      </div>

      {showEmployee && (
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title text-center">Gender wise Chart</h5>
                <div style={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexGender}
                        activeShape={renderActiveShape}
                        data={genderData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={110}
                        onMouseEnter={(_, index) => setActiveIndexGender(index)}
                        label={renderCustomizedLabel}
                      >
                        {genderData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={["#0088FE", "#FF8042", "#00C49F"][index % 3]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title text-center">Age-wise Chart</h5>
                <div style={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" fill="#8884d8" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title text-center">Experience-wise Chart</h5>
                <div style={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={expData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" fill="#00C49F" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
